import axios from 'axios'

import { ACCESS_TOKEN_KEY } from '@/constants/app'

// The API mounts every route under /v1; the dev server proxies that prefix.
const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/v1'
const USES_COOKIE_AUTH = import.meta.env.VITE_AUTH_MODE === 'cookie'

export class ApiError extends Error {
  constructor({ message, status, code, fieldErrors, isNetworkError = false }) {
    super(message)
    this.name = 'ApiError'
    this.status = status ?? null
    this.code = code ?? null
    /** Map of `fieldName -> message`, ready to feed into React Hook Form. */
    this.fieldErrors = fieldErrors ?? null
    this.isNetworkError = isNetworkError
  }

  get isUnauthorized() {
    return this.status === 401
  }

  get isForbidden() {
    return this.status === 403
  }

  get isNotFound() {
    return this.status === 404
  }

  get isValidationError() {
    return this.status === 422 || this.status === 400
  }
}

const STATUS_MESSAGES = {
  400: 'The request could not be processed. Check the highlighted fields.',
  401: 'Your session has expired. Sign in again to continue.',
  403: 'You do not have permission to perform this action.',
  404: 'The requested record could not be found.',
  409: 'This record was changed elsewhere. Reload and try again.',
  422: 'Some fields need attention before this can be saved.',
  429: 'Too many requests. Wait a moment and try again.',
  500: 'Something went wrong on our end. Try again shortly.',
}

/**
 * Accepts the common server error shapes and produces one predictable object so
 * feature code never branches on transport details.
 */
const normalizeError = (error) => {
  if (axios.isCancel(error)) {
    return new ApiError({ message: 'Request cancelled', code: 'CANCELLED' })
  }

  if (!error.response) {
    return new ApiError({
      message: 'Cannot reach the server. Check your connection and try again.',
      code: 'NETWORK',
      isNetworkError: true,
    })
  }

  const { status, data } = error.response
  // The API speaks RFC 9457 problem+json: `title` is the summary and `detail`
  // the specific cause, so the specific one wins. The `error`/`msg`/`message`
  // fallbacks cover the more common conventions.
  const message =
    data?.detail ??
    data?.error ??
    data?.msg ??
    data?.message ??
    data?.title ??
    STATUS_MESSAGES[status] ??
    'Unexpected error.'

  return new ApiError({
    message,
    status,
    code: data?.code,
    fieldErrors: toFieldErrors(data),
  })
}

/**
 * problem+json reports validation failures as
 * `errors: [{ pointer: '/email', message }]`. React Hook Form wants
 * `{ email: message }`, so the JSON pointer becomes a field name here rather
 * than in every form that displays one.
 */
const toFieldErrors = (data) => {
  if (Array.isArray(data?.errors)) {
    const entries = data.errors
      .filter((issue) => issue?.pointer)
      .map((issue) => [issue.pointer.replace(/^\//, '').replace(/\//g, '.'), issue.message])
    return entries.length > 0 ? Object.fromEntries(entries) : null
  }
  return data?.errors ?? data?.fieldErrors ?? null
}

export const apiClient = axios.create({
  baseURL: BASE_URL,
  timeout: 20000,
  /*
   * Always. Authentication is hybrid: the ACCESS token travels as a bearer
   * header, while the REFRESH token is an httpOnly cookie the browser must be
   * allowed to send. Gating this on VITE_AUTH_MODE meant the refresh cookie
   * was never attached and every session ended at the access token's expiry.
   */
  withCredentials: true,
  headers: { Accept: 'application/json' },
})

/**
 * Registered by <Auth> so a rejected token can end the session without this
 * module importing the store (which would import the API back).
 */
let onUnauthorized = null
export const setUnauthorizedHandler = (handler) => {
  onUnauthorized = handler
}

apiClient.interceptors.request.use((config) => {
  // The default Authorization header is set by services/jwt.service.js. This
  // covers the first request after a reload, before setSession has run.
  if (!USES_COOKIE_AUTH && !config.headers.Authorization) {
    const token = localStorage.getItem(ACCESS_TOKEN_KEY)
    if (token) config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

/**
 * Renews the access token, at most once at a time.
 *
 * Several requests usually fail together when a token expires, and each one
 * asking for a refresh would spend the rotating cookie several times over --
 * which the server correctly reads as REUSE and answers by revoking the whole
 * family. So concurrent callers share one in-flight refresh.
 */
let refreshInFlight = null

const renewAccessToken = () => {
  refreshInFlight ??= axios
    .post(`${BASE_URL}/auth/refresh`, null, { withCredentials: true })
    .then((response) => response.data?.access_token ?? null)
    .catch(() => null)
    .finally(() => {
      refreshInFlight = null
    })

  return refreshInFlight
}

/** Set by <Auth> so a renewed token reaches the store and localStorage. */
let onTokenRenewed = null
export const setTokenRenewedHandler = (handler) => {
  onTokenRenewed = handler
}

/** Refreshing is itself unauthenticated; retrying it would loop forever. */
const isAuthEndpoint = (url = '') => url.includes('/auth/refresh') || url.includes('/auth/login')

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const normalized = normalizeError(error)
    const request = error.config ?? {}

    /*
     * One retry, and only one. A 401 usually means the short access token
     * expired rather than that the session is over -- tearing the session
     * down on the first one logged people out mid-task, which is exactly the
     * rough edge the refresh flow exists to remove.
     */
    if (normalized.isUnauthorized && !request.__retried && !isAuthEndpoint(request.url)) {
      const token = await renewAccessToken()

      if (token) {
        onTokenRenewed?.(token)
        return apiClient({
          ...request,
          __retried: true,
          headers: { ...request.headers, Authorization: `Bearer ${token}` },
        })
      }
    }

    // Refresh was impossible or also refused: the session really is over.
    // Ending it lets the route guard redirect; no imperative navigation from
    // inside the transport layer.
    if (normalized.isUnauthorized) onUnauthorized?.()
    return Promise.reject(normalized)
  },
)

/**
 * Unwraps the envelopes this API uses — `{ result: ... }` and `{ data: ... }` —
 * while tolerating a bare payload.
 */
export const unwrap = (response) => {
  const body = response.data
  if (!body || typeof body !== 'object') return body
  if ('result' in body) return body.result
  if ('data' in body) return body.data
  return body
}

/**
 * Keeps the envelope intact for endpoints whose `meta` is part of the answer.
 *
 * The usage report is the case in point: `meta.totals` is computed over the
 * whole window rather than the returned page, so it is the only truthful
 * source for the summary tiles. `unwrap` would throw it away.
 */
export const unwrapEnvelope = (response) => {
  const body = response.data ?? {}
  return { data: body.data ?? body.result ?? body.items ?? [], meta: body.meta ?? {} }
}
